'use client'

import type React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useBarcode, useCart, useCheckout, useSearchParams } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import {
  CartFooter,
  CartHeader,
  CartHeaderActions,
  CartItems,
  CartSummary,
  CheckoutSteps,
} from '@/components/molecules'

interface CheckoutProps extends React.ComponentPropsWithoutRef<'div'> {
  focusBarcode?: () => void
  isCusRequired: boolean
}

export const Checkout = ({
  className,
  focusBarcode,
  isCusRequired,
  ...restProps
}: CheckoutProps) => {
  const { focusBarcodeInput } = useBarcode()
  const { cart } = useCart()
  const { isCheckout, showMobileCart } = useSearchParams()
  const { currentStep } = useCheckout()

  return (
    <div
      className={cn('flex flex-grow gap-6 lg:overflow-hidden', className)}
      {...restProps}
    >
      <div
        onClick={focusBarcodeInput}
        onKeyDown={focusBarcodeInput}
        className={cn(
          'flex w-full max-w-[var(--cart-products-width)] shrink-0 flex-col gap-5 lg:h-full',
          {
            hidden: isCheckout && showMobileCart,
          },
        )}
      >
        {currentStep !== 'SELECT_CUSTOMER' && (
          <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:gap-10">
            <CartHeader />
          </div>
        )}

        <CartItems />

        {cart?.cart_items?.length > 0 && <CartSummary />}
        {!isCheckout && <CartFooter />}
      </div>

      <AnimatePresence>
        {isCheckout && (
          <motion.div className="flex w-full flex-col items-center rounded-lg border border-gray-100/50 bg-gray-50 px-3 py-6 shadow-card dark:border-gray-700">
            <CheckoutSteps isCusRequired={isCusRequired} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
